// src/pages/imamMahdi/Misconceptions.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const Misconceptions = () => {
  return (
    <>
      <Helmet>
        <title>Common Misconceptions About Imam Mahdi (ajtf) | Ziyarat Guide</title>
        <meta
          name="description"
          content="Clarify common misconceptions about Imam Mahdi (ajtf) with compassion and authentic Islamic sources. Learn the true meaning of awaiting his reappearance."
        />
        <meta
          property="og:title"
          content="Common Misconceptions About Imam Mahdi (ajtf) | Ziyarat Guide"
        />
        <meta
          property="og:description"
          content="Understand the truth behind the common misconceptions about Imam Mahdi (ajtf) — his existence, purpose, and message of justice and hope."
        />
        <meta
          property="og:image"
          content="https://ziyaratguide.com/wp-content/uploads/2025/08/tength-Common-misconceptions-respectful-clarifications-.png"
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="imam-page">
        <div className="imam-content">
          <h1 className="page-title">Common Misconceptions About Imam Mahdi</h1>

          <div className="content-section">
            <img
              src="https://ziyaratguide.com/wp-content/uploads/2025/08/tength-Common-misconceptions-respectful-clarifications-.png"
              alt="Common Misconceptions About Imam Mahdi"
              className="imam-image"
            />

            <h2>Understanding with Compassion</h2>
            <p>
              Because the concept of Imam Mahdi (peace be upon him) spans many cultures, languages, and
              centuries—and appears in both Sunni and Shia traditions—it’s natural that various
              misunderstandings have arisen over time.
            </p>
            <p>
              It is important to approach these misconceptions with patience and sensitivity, offering
              calm clarifications rather than harsh debates.
            </p>

            <h3>Misconception 1: “Imam Mahdi is only a Shia belief.”</h3>
            <p>
              Clarification: The concept of the Mahdi—a divinely guided leader who will appear in the
              end times to establish justice—is found in both Sunni and Shia hadith collections,
              including Sahih al-Tirmidhi, Sunan Abu Dawud, and Musnad Ahmad ibn Hanbal. The main
              differences lie in details such as his lineage, occultation, and the Imams preceding him.
            </p>

            <h3>Misconception 2: “He is just a symbolic figure, not a real person.”</h3>
            <p>
              Clarification: In Shia Islam, Imam Mahdi is very much a living, real individual, born in
              255 AH (868 CE). He is currently in occultation by Allah’s command—hidden from the public
              eye for his protection—but continues to live and guide the faithful in unseen ways.
            </p>

            <h3>
              Misconception 3: “Belief in Imam Mahdi leads people to sit idle, waiting for him to fix
              everything.”
            </h3>
            <p>
              Clarification: Shia teachings actually stress the opposite. Awaiting Imam Mahdi means
              actively preparing—living truthfully, fighting oppression, helping the needy, and building
              a just society today, so that we may be worthy of serving him when he appears.
            </p>
            <blockquote>
              Imam Ja’far al-Sadiq (as) said: “Whoever waits for our matter and reforms himself, he is
              like one who struggles alongside the Mahdi with his sword.”
            </blockquote>

            <h3>Misconception 4: “Isn’t it strange or unbelievable that someone could live so long?”</h3>
            <p>
              Clarification: Allah is not bound by human biological norms. The Qur’an recounts the long
              lives of Prophets like Noah (as), who preached for 950 years, and the continued life of
              Jesus (as), who, by divine decree, has not yet experienced death. The occultation and
              prolonged life of Imam Mahdi are considered miracles within Allah’s power, consistent with
              the extraordinary nature of prophetic missions.
            </p>

            <h3>Misconception 5: “Do Shia believe Imam Mahdi will come with violence?”</h3>
            <p>
              Clarification: Imam Mahdi’s mission is fundamentally one of establishing justice,
              compassion, and truth. He will stand firmly against oppression, which may involve
              confronting tyrants—but his ultimate goal is to remove the roots of violence, greed, and
              injustice, creating a world where peace and equity prevail.
            </p>

            <h2>The Core Message</h2>
            <p>
              The belief in Imam Mahdi (aj) is a belief in hope—in a divinely promised future where the
              earth will finally be ruled by compassion, justice, and truth.
            </p>
            <p>
              It does not encourage passivity, nor does it promote blind conflict. Instead, it calls on
              every believer to purify their hearts and stand for justice today.
            </p>
          </div>
        </div>

        {/* Sidebar Navigation */}
        <aside className="imam-sidebar">
          <h2>Explore More</h2>
          <ul>
            <li>
              <Link to="/imam-mahdi">Who is Imam Mahdi?</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/described-in-quran-hadith">
                Described In Quran & Hadith
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/how-to-talk">How To Talk</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/impact-appearance">Impact of His Appearance</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/misconceptions">Misconceptions</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/practices-allegiance">Practices & Allegiance</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/reflections-stories">Reflections & Stories</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/return">Return</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/signs-reappearance">Signs of Reappearance</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/supporters">Supporters</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/ultimate-reformer">The Ultimate Reformer</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/why-occultation">Why Occultation</Link>
            </li>
          </ul>
        </aside>
      </div>
    </>
  );
};

export default Misconceptions;
